import { useTranslation } from 'react-i18next'
import type { ImportPrizeType } from '../ImportPrizes.types'
import { Box, Button, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import CardGiftcardOutlined from '@mui/icons-material/CardGiftcardOutlined'
import { PrizeDrawDetails } from 'types/prize-draw'
import { useMutation } from '@tanstack/react-query'
import { axiosPost } from 'connectors/axiosPost'
import { AxiosResponse } from 'axios'
import { useAuthContext } from 'context/AuthContext'
import { useAlert } from 'context/AlertContext'

type PrizeAssignedProps = {
  item: ImportPrizeType | PrizeDrawDetails['result'][number]
  name: string
  marketName: string
  type: 'prize-embargo' | 'prize-draw'
  refetch?: () => void
}

export const PrizeAssigned = ({ item, name, marketName, type, refetch }: PrizeAssignedProps) => {
  const { t } = useTranslation()
  const { token, refreshToken } = useAuthContext()
  const { changeMessage } = useAlert()

  const willRemain = item.amountLeft - item.winners.length * item.rewardsPerIssue

  const { mutate: sendTestEmail, isLoading: isSendingTests } = useMutation(
    async () =>
      await axiosPost<object | { error: AxiosResponse | undefined }>(
        token,
        refreshToken,
        `${type}/request/email/${item.id}`,
        {}
      ),
    {
      onSuccess: data => {
        if (data && 'error' in data) {
          changeMessage(`Failure during sending test email`, 'warning')
        } else {
          changeMessage('Test email sent', 'success')
        }
        refetch?.()
      },
    }
  )

  return (
    <Box width="100%">
      <Typography display="flex" alignItems="center" gap={2} variant="h6" fontWeight={600} mb={2}>
        <CardGiftcardOutlined />
        {name}
        {type === 'prize-draw' && (
          <Button disabled={isSendingTests} variant="outlined" color="secondary" onClick={() => sendTestEmail()}>
            {t('prizesManagement.actions.testEmails')}
          </Button>
        )}
      </Typography>
      <Grid container>
        <Grid item xs={12} md={3} lg={4} xl={3}>
          <Typography mb={2} color="gray" fontWeight={500}>
            {t('prize.periodTotal')}
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            {item.totalAmount}
          </Typography>
        </Grid>

        <Grid item xs={12} md={3} lg={4} xl={2}>
          <Typography mb={2} color="gray" fontWeight={500}>
            {t('prize.rewardsPerIssue')}
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            {item.rewardsPerIssue}
          </Typography>
        </Grid>

        <Grid item xs={12} md={3} lg={4} xl={2}>
          <Typography mb={2} color="gray" fontWeight={500}>
            {t('prize.processing')}
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            {item.winners.length}
          </Typography>
        </Grid>

        <Grid item xs={12} md={3} lg={4} xl={1}>
          <Typography mb={2} color="gray" fontWeight={500}>
            {t('prize.sent')}
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            {item.omit.length}
          </Typography>
        </Grid>

        <Grid item xs={12} md={3} lg={4} xl={1}>
          <Typography mb={2} color="gray" fontWeight={500}>
            {t('prize.available')}
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            {item.amountLeft}
          </Typography>
        </Grid>

        <Grid item xs={12} md={3} lg={4} xl={2}>
          <Typography mb={2} color={willRemain < 0 ? 'error.main' : 'gray'} fontWeight={500}>
            {t('prize.willRemain')}
          </Typography>
          <Typography variant="h6" fontWeight={600} color={willRemain < 0 ? 'error.main' : 'default'}>
            {willRemain}
          </Typography>
        </Grid>
      </Grid>

      {item.winners !== undefined ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ my: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'gray' }} component="th">
                  ID
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'gray' }} width="100%" component="th">
                  Customer
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'gray', minWidth: 180 }} component="th">
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'gray' }} component="th">
                  Market
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {item.winners.length > 0 || item.omit.length > 0 ? (
                [...item.omit, ...item.winners].map((winner, idx) => (
                  <TableRow key={idx}>
                    <TableCell>#{winner.ID}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2" display="block">
                        {winner.Email}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.omit.some(item => item.ID === winner.ID) ? 'Prize sent' : ''}</TableCell>
                    <TableCell>{marketName}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography textAlign="center" fontWeight={600}>
                      No entries
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Box>
  )
}
